import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, functions } from './firebase';

// The school shared password is verified in VR by the `authenticateSchool` Cloud
// Function. The secret itself lives in the `schoolSecrets` collection, which is
// denied to every client — including a super-admin on this dashboard — so there is
// no way to read a password back. The only client-visible signal is the
// `requiresPassword` flag on the school document, which is what these helpers read.
//
// Minimum length is enforced server-side by setSchoolPassword; mirrored here so the
// form can reject a short password before spending a round trip.
export const MIN_SCHOOL_PASSWORD_LENGTH = 4;

type SetSchoolPasswordRequest = {
  schoolId: string;
  newPassword: string;
};

type ClearSchoolPasswordRequest = {
  schoolId: string;
};

type SchoolPasswordResponse = {
  success: boolean;
};

const setSchoolPasswordCallable = httpsCallable<SetSchoolPasswordRequest, SchoolPasswordResponse>(
  functions,
  'setSchoolPassword'
);

const clearSchoolPasswordCallable = httpsCallable<
  ClearSchoolPasswordRequest,
  SchoolPasswordResponse
>(functions, 'clearSchoolPassword');

// The callables reject with a Firebase `functions/*` code. Map the ones a super-admin
// can realistically hit onto copy that says what to do about it, rather than leaking
// the raw internal message into a notification.
const SCHOOL_PASSWORD_ERRORS: Record<string, string> = {
  'functions/permission-denied': 'Only super-admins can change a school password.',
  'functions/unauthenticated': 'Your session has expired. Sign in again and retry.',
  'functions/not-found': 'That school no longer exists.',
  'functions/invalid-argument': `The password must be at least ${MIN_SCHOOL_PASSWORD_LENGTH} characters.`
};

const toSchoolPasswordError = (error: any): Error => {
  const code = String(error?.code ?? '');
  return new Error(
    SCHOOL_PASSWORD_ERRORS[code] ?? error?.message ?? 'Could not update the school password.'
  );
};

export const setSchoolPassword = async (schoolId: string, newPassword: string): Promise<void> => {
  try {
    await setSchoolPasswordCallable({ schoolId, newPassword });
  } catch (error) {
    throw toSchoolPasswordError(error);
  }
};

export const clearSchoolPassword = async (schoolId: string): Promise<void> => {
  try {
    await clearSchoolPasswordCallable({ schoolId });
  } catch (error) {
    throw toSchoolPasswordError(error);
  }
};

/**
 * Reads one school's current enforcement flag straight from Firestore, so a modal
 * opened from a stale table row still shows the truth.
 */
export const getSchoolRequiresPassword = async (schoolId: string): Promise<boolean> => {
  const snapshot = await getDoc(doc(db, 'schools', schoolId));
  return snapshot.exists() && snapshot.data()?.requiresPassword === true;
};

/**
 * Reads the enforcement flag for every school as an id -> boolean map.
 *
 * The schools list this dashboard renders comes from the Postgres mirror, which does
 * not carry `requiresPassword` (it is written to Firestore by the Cloud Function and
 * never synced), so the flags are fetched separately and merged into the table rows.
 */
export const getSchoolPasswordFlags = async (): Promise<Record<string, boolean>> => {
  const snapshot = await getDocs(collection(db, 'schools'));
  return snapshot.docs.reduce<Record<string, boolean>>((flags, schoolDoc) => {
    flags[schoolDoc.id] = schoolDoc.data()?.requiresPassword === true;
    return flags;
  }, {});
};
